import { useEffect, useId, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/connection-access';
import { sharingApi } from '../sharing-api';
import type { ConnectionAccessChoice } from '../connection-read-access/api';
import './policy.css';

export type ConnectionPerson = { userId: string; displayName: string | null; email: string };

export function useConnectionPeople() {
  const [people, setPeople] = useState<ConnectionPerson[]>([]);
  useEffect(() => {
    let current = true;
    void sharingApi
      .colleagues()
      .then(({ members }) => {
        if (current) setPeople(Array.isArray(members) ? members : []);
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, []);
  return people;
}

export function ConnectionPolicyFields({
  value,
  onChange,
  companyName,
  canCompany,
  ownerUserId,
  people,
  disabled = false,
  allowPeople = true,
  readOnly = false,
  writeUnavailableReason,
}: {
  value: ConnectionAccessChoice;
  onChange: (value: ConnectionAccessChoice) => void;
  companyName: string;
  canCompany: boolean;
  ownerUserId: string | null;
  people: ConnectionPerson[];
  disabled?: boolean;
  allowPeople?: boolean;
  readOnly?: boolean;
  writeUnavailableReason?: string;
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const group = useId();
  const [search, setSearch] = useState('');
  const selected = new Set([ownerUserId, ...value.people]);
  const choices = people.filter(
    (person) =>
      !selected.has(person.userId) &&
      `${person.displayName ?? ''} ${person.email}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()),
  );
  const setAudience = (audience: ConnectionAccessChoice['audience']) =>
    onChange({ ...value, audience, people: audience === 'people' ? value.people : [] });
  return (
    <div className="ws-access-fields">
      <fieldset disabled={disabled}>
        <legend>{copy.qWho}</legend>
        {(
          [
            ['private', copy.optOnlyMe, copy.optOnlyMeBody],
            [
              'company',
              formatCopy(copy.optEveryone, { company: companyName }),
              formatCopy(copy.optEveryoneBody, { company: companyName }),
            ],
            ...(allowPeople ? [['people', copy.optPeople, ''] as const] : []),
          ] as const
        ).map(([audience, label, body]) => (
          <label
            className="ws-access-option"
            key={audience}
            data-selected={value.audience === audience}
          >
            <input
              type="radio"
              name={`${group}-audience`}
              value={audience}
              checked={value.audience === audience}
              disabled={audience === 'company' && !canCompany}
              onChange={() => setAudience(audience)}
            />
            <span>
              {label}
              <small>{audience === 'company' && !canCompany ? copy.everyoneAdminOnly : body}</small>
            </span>
          </label>
        ))}
        {allowPeople && value.audience === 'people' && (
          <div className="ws-access-people">
            <label>
              <span className="sr-only">{copy.addPeople}</span>
              <input
                type="search"
                placeholder={copy.addPeople}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <ul>
              {[...selected]
                .filter((id): id is string => Boolean(id))
                .map((id) => {
                  const person = people.find((person) => person.userId === id);
                  if (!person) return null;
                  const name = person.displayName || person.email;
                  return (
                    <li key={id}>
                      <span>
                        <strong>{name}</strong>
                        <small>{person.email}</small>
                      </span>
                      {id === ownerUserId ? (
                        <small>{copy.ownerTag}</small>
                      ) : (
                        <button
                          type="button"
                          className="ws-text-button"
                          aria-label={`${copy.remove}: ${name}`}
                          onClick={() =>
                            onChange({
                              ...value,
                              people: value.people.filter((personId) => personId !== id),
                            })
                          }
                        >
                          ×
                        </button>
                      )}
                    </li>
                  );
                })}
            </ul>
            {search.trim() && (
              <ul className="ws-access-person-results">
                {choices.map((person) => (
                  <li key={person.userId}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange({ ...value, people: [...value.people, person.userId] });
                        setSearch('');
                      }}
                    >
                      <strong>{person.displayName || person.email}</strong>
                      <small>{person.email}</small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!value.people.length && <p className="ws-access-note">{copy.peopleEmpty}</p>}
          </div>
        )}
      </fieldset>
      {!readOnly && (
        <fieldset disabled={disabled}>
          <legend>{copy.qWhat}</legend>
          {(
            [
              ['ask', copy.optRead, copy.optReadBody],
              ['allowed', copy.optReadWrite, copy.optReadWriteBody],
            ] as const
          ).map(([changes, label, body]) => (
            <label
              key={changes}
              className="ws-access-option"
              data-selected={value.changes === changes}
            >
              <input
                type="radio"
                name={`${group}-changes`}
                checked={value.changes === changes}
                value={changes}
                disabled={changes === 'allowed' && Boolean(writeUnavailableReason)}
                onChange={() => onChange({ ...value, changes })}
              />
              <span>
                {label}
                <small>
                  {changes === 'allowed' && writeUnavailableReason ? writeUnavailableReason : body}
                </small>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
}
